
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteRepositoryInterface, {
  Website,
  WebsiteAlias,
  WebsiteComposite,
  WebsiteCreation,
  WebsiteCreationAction,
  WebsiteCreationActionStatus,
  WebsiteCreationStatus,
  WebsiteDeletionAction,
  WebsiteDetails,
  WebsiteEnvironment,
  WebsiteGroup,
  WebsiteStaging,
  WebsiteStatus,
  WebsiteToggle,
  WebsiteToggleAction,
  WebsiteToggleStatus,
} from '../../../services/Website/interfaces';
import VComponent from '@/components/base/VComponent.vue';
import StagingDialog from '@/components/dialogs/website/StagingDialog.vue';
import LogsDialog from '@/components/dialogs/website/LogsDialog.vue';
import WebsiteLogRepositoryInterface from '@/services/WebsiteLog/interfaces';
import WebsiteStagingRepositoryInterface, { StagingCreateEnvironment } from '@/services/WebsiteStaging/interfaces';
import WpInstallDialog from '@/components/dialogs/website/WpInstallDialog.vue';
import DeleteWebsiteDialog from '@/components/dialogs/website/DeleteWebsiteDialog.vue';
import AliasDialog from '@/components/dialogs/website/AliasDialog.vue';
import ToggleWebsiteDialog from '@/components/dialogs/website/ToggleWebsiteDialog.vue';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import SubwebsiteDialog from '@/components/dialogs/website/SubwebsiteDialog.vue';
import RecreateWebsiteDialog from '@/components/dialogs/website/RecreateWebsiteDialog.vue';
import RetryWebsiteCreationActionDialog from '@/components/dialogs/website/RetryWebsiteCreationActionDialog.vue';
import RenameWebsiteDialog from '@/components/dialogs/website/RenameWebsiteDialog.vue';
import WebsiteMigrationsRepositoryInterface from '@/services/WebsiteMigrations/interfaces';
import WPCliRepositoryInterface, {
  TemporaryWPCredentialsInterface,
  WPCheckResultsInterface,
} from '@/services/WPCli/interfaces';
import RedisCacheRepositoryInterface, {
  RedisCacheStatusEnum, StatusUpdateErrorInterface, StatusUpdateResultInterface, SyncStatusErrorInterface,
  SyncStatusResultInterface,
} from '@/services/RedisCache/interfaces/redis-cache.interface';
import RedisCacheManagementDialog from '@/views/pages/redisCache/RedisCacheManagementDialog.vue';
import WpUninstallDialog from '@/components/dialogs/website/WpUninstallDialog.vue';

interface WebsiteLogsMessageInterface {
  websiteId: number,
  domain: string,
  logs: string[],
}

interface WebsiteCreationActionMessagePayloadInterface {
  id: number,
  status: string,
  logs: string[],
}

interface WebsiteCreationMessagePayloadInterface {
  id: number,
  websiteId: number,
  status: string,
  actions: WebsiteCreationAction[],
}

interface WebsiteCreationMessageInterface {
  payload: WebsiteCreationMessagePayloadInterface;
}

interface WebsiteCreationActionMessageInterface {
  payload: WebsiteCreationActionMessagePayloadInterface;
}


@Component({
  computed: {
    WebsiteStatus() {
      return WebsiteStatus;
    },
    RedisCacheStatusEnum() {
      return RedisCacheStatusEnum;
    },
  },
  mixins: [Themeable],
  inject: [
    'websiteRepository',
    'redisCacheRepository',
    'websiteLogRepository',
    'websiteStagingRepository',
    'serverRepository',
    'websiteMigrationsRepository',
    'wpCliRepository',
  ],
  components: {
    RedisCacheManagementDialog,
    SubwebsiteDialog,
    VComponent,
    StagingDialog,
    LogsDialog,
    WpInstallDialog,
    WpUninstallDialog,
    DeleteWebsiteDialog,
    AliasDialog,
    ToggleWebsiteDialog,
    RecreateWebsiteDialog,
    RetryWebsiteCreationActionDialog,
    RenameWebsiteDialog,
  },
  sockets: {
    websiteCreation(msg: WebsiteCreationMessageInterface): void {
      this.$emit('creationReceived', msg);
    },
    requestLogs(msg: WebsiteLogsMessageInterface): void {
      this.$emit('logsReceived', msg);
    },
    websiteCreationAction(msg: WebsiteCreationActionMessageInterface): void {
      this.$emit('actionReceived', msg.payload);
    },
    websiteDeletion(msg: { payload: WebsiteDeletionAction[] }): void {
      this.$emit('websiteDeleting', msg.payload);
    },
    websiteToggle(msg: { payload: WebsiteToggle }): void {
      this.$emit('websiteToggle', msg.payload);
    },
    websiteToggleAction(msg: { payload: WebsiteToggleAction }): void {
      this.$emit('websiteToggleAction', msg.payload);
    },
    wpCheckResult(msg: { payload: WPCheckResultsInterface }): void {
      this.$emit('resultsReceived', msg.payload);
    },
    getRedisCacheStatus(msg: { payload: SyncStatusResultInterface }): void {
      this.$emit('redisCacheStatusReceived', msg.payload);
    },
    getRedisCacheStatusError(msg: { payload: SyncStatusErrorInterface }): void {
      this.$emit('redisCacheStatusErrorReceived', msg.payload);
    },
    redisCacheStatusUpdated(msg: { payload: StatusUpdateResultInterface }): void {
      this.$emit('redisCacheStatusUpdateReceived', msg.payload);
    },
    redisCacheStatusUpdateError(msg: { payload: StatusUpdateErrorInterface }): void {
      this.$emit('redisCacheStatusUpdateErrorReceived', msg.payload);
    },
  },
})
export default class ViewWebsite extends Vue {
  public loading: boolean = true;

  public readonly websiteRepository!: WebsiteRepositoryInterface;

  public readonly redisCacheRepository!: RedisCacheRepositoryInterface;

  public readonly websiteLogRepository!: WebsiteLogRepositoryInterface;

  public readonly websiteStagingRepository!: WebsiteStagingRepositoryInterface;

  public readonly serverRepository!: ServerRepositoryInterface;

  public readonly websiteMigrationsRepository!: WebsiteMigrationsRepositoryInterface;

  public readonly wpCliRepository!: WPCliRepositoryInterface;

  public websiteId: number = 0;

  public website: Website = {} as Website;

  public details: WebsiteDetails = {} as WebsiteDetails;

  public staging: WebsiteStaging = {} as WebsiteStaging;

  public alias: WebsiteAlias | undefined = undefined;

  public group: WebsiteGroup | undefined = undefined;

  public stagingDialog: string = '';

  public domainForStagingDialog: string = '';

  public logsDialog: string = '';

  public domainForLogsDialog: string = '';

  public logsLoading: boolean = false;

  public logs: string[] = [];

  public creations: WebsiteCreation[] = [];

  public deletionActions: WebsiteDeletionAction[] = [];

  public creationActionsMap: { [key: number]: string } = {};

  public deletionActionsMap: { [key: number]: string } = {};

  public redisCacheStatus: RedisCacheStatusEnum = RedisCacheStatusEnum.UNKNOWN;

  private redisCacheManagementDialog: string = '';

  public websiteDeleted: boolean = false;

  public actionsMap: { [key: string]: string } = {
    'create_dns': 'CREATE DNS',
    'delete_dns': 'DELETE DNS',
    'create_unix_user': 'CREATE UNIX USER',
    'delete_unix_user': 'DELETE UNIX USER',
    'create_database': 'CREATE DATABASE',
    'delete_database': 'DELETE DATABASE',
    'create_vhost': 'CREATE VHOST',
    'delete_vhost': 'DELETE VHOST',
    'create_redis_cache': 'CREATE REDIS CACHE',
    'delete_redis_cache': 'DELETE REDIS CACHE',
    'create_email': 'CREATE EMAIL',
    'delete_email': 'DELETE EMAIL',
    'create_letsencrypt': 'CREATE LETS ENCRYPT',
    'delete_letsencrypt': 'DELETE LETS ENCRYPT',
    'activate_cdn': `ACTIVATE CDN`,
    'deactivate_cdn': `DEACTIVATE CDN`,
    'install_wp': 'INSTALL WP',
    'uninstall_wp': 'UNINSTALL WP',
    'clone_wp': 'CLONE WP',
    'delete_ftp': 'DELETE FTP',
  };

  public toggleMap: { [key: string]: string } = {
    'vhost': 'ORIGIN VHOST',
    'database_users': 'DATABASE USERS',
    'mail_users': 'MAIL USERS',
    'ftp_users': 'FTP USERS',
    'unix_user': 'UNIX USER',
  };

  public actionsStatusMap: { [key: string]: string } = {
    'pending': 'PENDING',
    'in progress': 'IN PROGRESS',
    'failed': 'FAILED',
    'retry': 'RETRY',
    'retrying': 'RETRYING',
    'done': 'DONE',
    'rolling back': 'ROLLING BACK',
    'rolled back': 'ROLLED BACK',
    'cancelled': 'CANCELLED',
  };

  public actionStatusIconMap: { [key: string]: string } = {
    'pending': 'mdi-progress-clock',
    'in progress': 'mdi-progress-helper',
    'failed': 'mdi-close-thick',
    'retry': 'mdi-repeat',
    'retrying': 'mdi-repeat-variant',
    'done': 'mdi-check',
    'rolling back': 'mdi-arrow-u-left-top',
    'rolled back': 'mdi-backup-restore',
    'cancelled': 'mdi-cancel',
  };

  public statusStateMap: { [key: string]: string } = {
    enable: 'ACTIVE',
    disable: 'DISABLED',
  };

  public wpInstallDialog: string = '';

  public websiteForInstallWPDialog: Website = {} as Website;

  public wpUninstallDialog: string = '';

  public websiteIdForUninstallWPDialog: number = 0;

  public deleteWebsiteDialog: string = '';

  public websiteForDeleteDialog: Website = {} as Website;

  public aliasDialog: string = '';

  public domainForAliasDialog: string = '';

  public subwebsiteDialog: string = '';

  public toggleWebsiteDialog: string = '';

  public websiteForToggleDialog: Website = {} as Website;

  public recreateWebsiteDialog: string = '';

  public websiteForRecreateDialog: Website = {} as Website;

  public retryWebsiteCreationActionDialog: string = '';

  public domainForRetryWebsiteCreationActionDialog: string = '';

  public creationIdForRetryWebsiteCreationActionDialog: number = -1;

  public actionForRetryWebsiteCreationActionDialog: WebsiteCreationAction = {} as WebsiteCreationAction;

  public renameWebsiteDialog: string = '';

  public toggles: WebsiteToggle[] = [];

  public server: ServerInterface = {} as unknown as ServerInterface;

  public get toggleWebsiteMessage(): string | undefined {
    switch (this.website.status) {
      case 'ACTIVE':
        return 'DISABLE';
      case 'DISABLED':
        return 'ACTIVATE';
      default:
        return this.toggles[this.toggles.length - 1]?.state || undefined;
    }
  }

  public stateStatusReverseMap: { [key: string]: string } = {
    ACTIVE: 'DISABLED',
    DISABLED: 'ACTIVE',
  };

  public async created(): Promise<void> {
    this.websiteId = Number(this.$route.params['id']);
    const [websiteData, redisCacheData] = await this.getWebsiteRelatedData();
    this.website = websiteData.website;
    this.details = websiteData.details;
    this.staging = websiteData.staging;
    this.alias = websiteData.alias;
    this.group = websiteData.group;
    this.server = await this.serverRepository.getServer(websiteData.website.originServer);
    if (redisCacheData !== undefined) {
      this.redisCacheStatus = redisCacheData.status;
    }
    await this.getCreation();
    await this.getToggles();
    this.$on('creationReceived', this.handleCreationMessage);
    this.$on('logsReceived', this.handleWSLogsMessage);
    this.$on('actionReceived', this.handleWSActionMessage);
    this.$on('websiteDeleting', this.handleWebsiteDeleting);
    this.$on('websiteToggle', this.handleWebsiteToggle);
    this.$on('websiteToggleAction', this.handleWebsiteToggleAction);
    this.$on('redisCacheStatusReceived', this.handleRedisCacheSyncStatusResult);
    this.$on('redisCacheStatusErrorReceived', this.handleRedisCacheSyncStatusErrorResult);
    this.$on('redisCacheStatusUpdateReceived', this.handleRedisCacheUpdateStatusResult);
    this.$on('redisCacheStatusUpdateErrorReceived', this.handleRedisCacheUpdateStatusErrorResult);
    this.loading = false;
  }

  private async getWebsiteRelatedData(): Promise<[WebsiteComposite, SyncStatusResultInterface | undefined]> {
    const website = await this.websiteRepository.getWebsiteById(this.websiteId);
    const redisCacheStatus = await this.fetchRedisCacheStatus();
    return [website, redisCacheStatus];
  }

  private async fetchRedisCacheStatus(): Promise<SyncStatusResultInterface | undefined> {
    try {
      return await this.redisCacheRepository.getRedisCacheStatusByWebsiteId(this.websiteId);
    } catch (err) {
      if (err?.response?.status === 404) {
        this.redisCacheStatus = RedisCacheStatusEnum.DISABLED;
      } else {
        this.$errorDialog.show(
          `Error while fetching redis cache status: ${err?.response?.data?.error || err.message}`,
        );
      }
    }
  }

  public handleCreationMessage(msg: WebsiteCreationMessageInterface): void {
    if (!msg.payload.websiteId) {
      const index = this.creations.findIndex((creation) => creation.id === msg.payload.id);
      if (index === -1) {
        return;
      }

      this.creations[index].status = msg.payload.status;
      if (msg.payload.status === WebsiteCreationStatus.FAILED) {
        this.website.status = WebsiteStatus.ERROR;

        this.creations[index].actions.forEach((action, actionIndex) => {
          if (action.status === WebsiteCreationActionStatus.PENDING) {
            this.creations[index].actions[actionIndex].status = WebsiteCreationActionStatus.CANCELLED;
          }
        });

        return;
      }

      if (msg.payload.status === WebsiteCreationStatus.DONE) {
        this.website.status = WebsiteStatus.ACTIVE;
      }
    }

    if (msg.payload.websiteId !== this.websiteId) {
      return;
    }

    this.creations.unshift({
      id: msg.payload.id,
      websiteId: msg.payload.websiteId,
      status: WebsiteCreationStatus.PENDING,
      actions: msg.payload.actions,
      createdDate: new Date().toISOString(),
      updatedDate: new Date().toISOString(),
    });

    msg.payload.actions.forEach((action) => {
      this.creationActionsMap[action.id] = WebsiteCreationActionStatus.PENDING;
    });

    return;
  }

  public handleWSLogsMessage(msg: WebsiteLogsMessageInterface): void {
    if (msg.websiteId !== this.website.id) {
      return;
    }

    if (!msg.logs || !msg.logs.length) {
      this.$infoDialog.show('There are no logs for this website!');
    }

    this.logs = msg.logs;
    this.logsLoading = false;
  }

  public handleWSActionMessage(action: WebsiteCreationActionMessagePayloadInterface): void {
    if (this.creationActionsMap[action.id]) {
      this.creations.forEach((_, index) => {
        const actionIndex = this.creations[index].actions.findIndex((creationAction) => creationAction.id === action.id);
        if (actionIndex === -1) {
          return;
        }

        this.creations[index].actions[actionIndex].status = action.status;

        if (!action.logs || !action.logs.length) {
          return;
        }

        if (!this.creations[index].actions[actionIndex].logs) {
          this.creations[index].actions[actionIndex].logs = [];
        }

        this.creations[index].actions[actionIndex].logs.push(
          ...action.logs.map((l) => ({ id: -1, log: l, createdDate: new Date().toISOString() })),
        );
      });

      return;
    }

    if (this.deletionActionsMap[action.id]) {
      const index = this.deletionActions.findIndex((deletionAction) => deletionAction.id === action.id);
      if (index === -1) {
        return;
      }

      this.deletionActions[index].status = action.status;

      this.websiteDeleted = this.deletionActions.every((deletionAction) => deletionAction.status === 'done');

      if (!action.logs || !action.logs.length) {
        return;
      }

      if (!this.deletionActions[index].logs) {
        this.deletionActions[index].logs = [];
      }

      this.deletionActions[index].logs.push(
        ...action.logs.map((l) => ({ id: -1, log: l, createdDate: new Date().toISOString() })),
      );
    }
  }

  private isCreationLatest(creation: WebsiteCreation, creations: WebsiteCreation[]): boolean {
    const ids = creations.map(creation => creation.id);

    const latest = ids.length
      ? ids.reduce((latest, id) => Math.max(latest, id))
      : -1;

    return latest !== -1 && latest === creation.id;
  }

  private async showRedisCacheManagementDialog(): Promise<void> {
    try {
      this.redisCacheManagementDialog = 'redis-cache-management-dialog';
    } catch (err) {
      this.$errorDialog.show(
        `Error while trying to manage redis cache: ${err.message}`,
      );
    }
  }

  private async destroyRedisCacheManagementDialog(): Promise<void> {
    this.redisCacheManagementDialog = '';
  }

  private async onRedisCacheManagementDialogToggle(enable: boolean): Promise<void> {
    const prevStatus = this.redisCacheStatus;
    try {
      if (enable) {
        this.redisCacheStatus = RedisCacheStatusEnum.PENDING_ENABLE;
        return await this.redisCacheRepository.enableRedisCacheByWebsiteId(this.websiteId);
      }
      this.redisCacheStatus = RedisCacheStatusEnum.PENDING_DISABLE;
      await this.redisCacheRepository.disableRedisCacheByWebsiteId(this.websiteId);
    } catch (err) {
      this.redisCacheStatus = prevStatus;
      this.$errorDialog.show(
        `Error while managing redis cache: ${err?.response?.data?.error || err.message}`,
      );
    }
  }

  private async onRedisCacheManagementDialogGetStatus(): Promise<void> {
    try {
      await this.redisCacheRepository.getRedisCacheSyncedStatusByWebsiteId(this.websiteId);
    } catch (err) {
      this.$errorDialog.show(
        `Error while synchronizing redis cache status: ${err?.response?.data?.error || err.message}`,
      );
    }
  }

  public handleRedisCacheSyncStatusResult(data: SyncStatusResultInterface): void {
    this.redisCacheStatus = data.status;
  }

  public handleRedisCacheSyncStatusErrorResult(data: SyncStatusErrorInterface): void {
    this.$errorDialog.show(
      `Error while synchronizing redis cache status: ${data.reason}`,
    );
  }

  public handleRedisCacheUpdateStatusResult(data: StatusUpdateResultInterface): void {
    this.redisCacheStatus = data.status;
  }

  public handleRedisCacheUpdateStatusErrorResult(data: StatusUpdateErrorInterface): void {
    this.redisCacheStatus = data.currentStatus;
    this.$errorDialog.show(
      `Error while updating redis cache status: ${data.reason}`,
    );
  }

  public async getCreation(): Promise<void> {
    try {
      const creations = await this.websiteRepository.getWebsiteCreations(this.websiteId);
      if (!creations || !creations.length) {
        return;
      }

      this.creations = creations;
      this.creations.forEach((creation) => {
        creation.actions.forEach((action) => this.creationActionsMap[action.id] = action.status);
      });
    } catch (err) {
      this.$errorDialog.show(
        `Error while getting website creation actions: ${err?.response?.data?.error || err.message}`,
      );
    }
  }

  public async getToggles(): Promise<void> {
    try {
      const toggles = await this.websiteRepository.getWebsiteToggles(this.websiteId);
      if (!toggles || !toggles.length) {
        return;
      }

      this.toggles = toggles;
    } catch (err) {
      this.$errorDialog.show(
        `Error while getting website toggles: ${err?.response?.data?.error || err.message}`,
      );
    }
  }

  public showStagingDialog(): void {
    this.domainForStagingDialog = this.website.domain;
    this.stagingDialog = 'staging-dialog';
  }

  public async createStaging(environment: StagingCreateEnvironment): Promise<void> {
    try {
      if (environment.type === "new") {
        const tmpCreds: TemporaryWPCredentialsInterface = {
          domain: `staging.${this.website.domain}`,
          email: environment.wpEmail,
          user: environment.wpUsername,
          password: environment.wpPassword,
        };
        console.log(tmpCreds);
        await this.wpCliRepository.saveTempCredentials(tmpCreds);
      }

      await this.websiteRepository.createWebsiteStaging(this.website.id, environment.type, this.$store.getters.id);
      this.$successDialog.show('Staging website creation started!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showLogsDialog(): void {
    this.domainForLogsDialog = this.website.domain;
    this.logsDialog = 'logs-dialog';
  }

  public async requestLogs(data: { type: string, lines: number }): Promise<void> {
    try {
      await this.websiteLogRepository.requestLogs(this.website.id, data.type, data.lines, this.$store.getters.id);
      this.logsLoading = true;
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public async mergeStaging(): Promise<void> {
    try {
      await this.websiteStagingRepository.mergeStaging(this.website.id, this.$store.getters.id);
      this.$successDialog.show(`Staging Merge Started!`);
      await this.$router.push({ name: 'website_all' });
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showInstallWPDialog(): void {
    this.website.details = this.details;
    this.websiteForInstallWPDialog = this.website;
    this.wpInstallDialog = 'wp-install-dialog';
  }

  public showUninstallWPDialog(): void {
    this.websiteIdForUninstallWPDialog = this.website.id;
    this.wpUninstallDialog = 'wp-uninstall-dialog';
  }

  public showDeleteWebsiteDialog(): void {
    this.websiteForDeleteDialog = this.website;
    this.deleteWebsiteDialog = 'delete-website-dialog';
  }

  public async deleteWebsite(website: Website): Promise<void> {
    try {
      await this.websiteRepository.deleteWebsite(website.id);
      this.$successDialog.show('Website Deletion Started!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public handleWebsiteDeleting(deletionActions: WebsiteDeletionAction[]): void {
    if (deletionActions[0].deletion.websiteId !== this.websiteId) {
      return;
    }

    deletionActions.forEach((deletionAction) => {
      this.deletionActionsMap[deletionAction.id] = deletionAction.status;
    });

    this.deletionActions = deletionActions;
  }

  public handleWebsiteToggle(toggle: WebsiteToggle): void {
    if (toggle.websiteId !== this.websiteId) {
      return;
    }

    const index = this.toggles.findIndex((t) => t.id === toggle.id);
    if (index > -1) {
      this.toggles[index].status = toggle.status;
      if (toggle.status === WebsiteToggleStatus.DONE) {
        this.website.status = this.statusStateMap[toggle.state];
      }

      return;
    }

    this.toggles.push(toggle);
  }

  public handleWebsiteToggleAction(toggleAction: WebsiteToggleAction): void {
    if (!this.toggles.length) {
      return;
    }

    this.toggles.forEach((toggle, toggleIndex) => {
      const actionIndex = toggle.actions.findIndex((tA) => tA.id === toggleAction.id);
      if (actionIndex === -1) {
        return;
      }

      this.toggles[toggleIndex].actions[actionIndex].status = toggleAction.status;
      this.toggles[toggleIndex].actions[actionIndex].logs = toggleAction.logs;
    });
  }

  public showAliasDialog(): void {
    this.domainForAliasDialog = this.website.domain;
    this.aliasDialog = 'alias-dialog';
  }

  public async createAlias(domain: string, excludeActions: string[]): Promise<void> {
    try {
      await this.websiteRepository.createWebsiteAlias(
        this.website.id,
        domain,
        excludeActions,
        WebsiteEnvironment.ALIAS,

        this.$store.getters.id,
      );
      this.$successDialog.show('Alias website creation started!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showSubwebsiteDialog(): void {
    this.subwebsiteDialog = 'subwebsite-dialog';
  }

  public async createSubwebsite(data: { domain: string, excludeActions: string[] }): Promise<void> {
    try {
      await this.websiteRepository.createSubwebsite(
        this.website.id,
        `${data.domain}.${this.website.domain}`,
        this.$store.getters.id,
        WebsiteEnvironment.SUB,
        data.excludeActions,
      );
      this.$successDialog.show('Subwebsite creation started!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showToggleDialog(): void {
    this.websiteForToggleDialog = this.website;
    this.toggleWebsiteDialog = 'toggle-website-dialog';
  }

  public async toggleWebsite(): Promise<void> {
    try {
      const status = this.stateStatusReverseMap[this.website.status] ? this.stateStatusReverseMap[this.website.status]
        : this.statusStateMap[this.toggles[this.toggles.length - 1].state];

      await this.websiteRepository.toggleWebsite(
        this.website.id,
        status,
      );
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showRecreateWebsiteDialog(): void {
    this.websiteForRecreateDialog = this.website;
    this.recreateWebsiteDialog = 'recreate-website-dialog';
  }

  public async recreateWebsite(): Promise<void> {
    try {
      await this.websiteRepository.recreateWebsite(this.website.id);
      this.$successDialog.show('Website Recreation Started');
      this.website.status = WebsiteStatus.PENDING;
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public canRetryAction(action: WebsiteCreationAction): boolean {
    if ([
      WebsiteCreationActionStatus.PENDING,
      WebsiteCreationActionStatus.IN_PROGRESS,
      WebsiteCreationActionStatus.RETRY,
      WebsiteCreationActionStatus.RETRYING,
      WebsiteCreationActionStatus.ROLLING_BACK,
      WebsiteCreationActionStatus.DONE,
    ].includes(action.status as WebsiteCreationActionStatus)) {
      return false;
    }

    if (!action.parentActionsIds || !action.parentActionsIds.length) {
      return true;
    }

    return action.parentActionsIds.every((parentActionId) => {
      const parentAction = this.creations[0].actions.find((action) => action.id === parentActionId);

      return !(!parentAction || parentAction.status !== WebsiteCreationActionStatus.DONE);
    });
  }

  public showRetryDialog(creationId: number, action: WebsiteCreationAction): void {
    this.retryWebsiteCreationActionDialog = 'retry-website-creation-action-dialog';
    this.domainForRetryWebsiteCreationActionDialog = this.website.domain;
    this.creationIdForRetryWebsiteCreationActionDialog = creationId;
    this.actionForRetryWebsiteCreationActionDialog = action;
  }

  public async retryAction(creationId: number, action: WebsiteCreationAction): Promise<void> {
    try {
      await this.websiteRepository.retryAction(this.website.id, creationId, action.id);

      this.creations[0].status = WebsiteCreationStatus.PENDING;
      this.creations[0].actions.forEach((a, index) => {
        if (a.id !== action.id) {
          return;
        }

        this.creations[0].actions[index].status = WebsiteCreationActionStatus.RETRY;
      });

      this.$successDialog.show(`Retry website creation action ${action.action} started`);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showRenameDialog(): void {
    this.renameWebsiteDialog = 'rename-website-dialog';
  }

  public async renameWebsite(newDomain: string, excludeActions: string[]): Promise<void> {
    try {
      await this.websiteMigrationsRepository.renameWebsite(this.website.id, newDomain, excludeActions);

      this.$successDialog.show('Website Rename Started');

      await this.$router.push({ name: 'website_migrations_list' });
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }
}
